import React, { useEffect, useMemo, useState } from "react";
import MediumCard from "components/ModularCards/MediumCard";
import { LargeCard } from "components/ModularCards/LargeCard";
import { SmallCard } from "components/ModularCards/SmallCard";
import { XLargeCard } from "components/ModularCards/XLargeCard";
import { useFirebase } from "api/FirebaseProvider";
import { useParams } from "react-router-dom";
import EMLogo from "resources/EMLogo";
import { AnimatePresence, motion } from "framer-motion";
import Menu from "./Menu";
import SiteInformation from "./SiteInformation";
import { Dashboard, Page } from "model/Datatypes";
import useSiteSetup from "useSiteSetup";
import { useGlobalState } from "AppStore";

interface Props {}

const TouchLayout: React.FC<Props> = () => {
  useSiteSetup();
  const fb = useFirebase();
  const storage = fb.storage();
  const { dashboards } = useDashboards();
  const { type } = useGlobalState();
  const [dashboard, setDashboard] = useState<Dashboard>();
  const [backgroundImg, setBackgroundImg] = useState<string>();
  const [currentPage, setCurrentPage] = useState<Page | null>(null);
  const [animationDirection, setAnimationDirection] = useState<"left" | "right">("right");

  const { site } = useParams<{
    site: string | undefined;
  }>();

  // set background image
  useEffect(() => {
    if (dashboard) {
      const storageRef = storage.ref(`${dashboard.backgroundImgUrl}`);
      storageRef
        .getDownloadURL()
        .then((url) => {
          setBackgroundImg(url);
        })
        .catch((err) => console.log(err));
    }
  }, [site, storage, dashboard]);

  // Load the correct dashboard
  useEffect(() => {
    dashboards?.forEach((dashboard) => {
      if (site === dashboard.site) {
        setDashboard(dashboard);
      }
    });
  }, [dashboards, site]);

  useEffect(() => {
    if (dashboard) setCurrentPage(dashboard.pages[0]);
  }, [dashboard]);

  const handlePageChange = (page: Page) => {
    setCurrentPage(page);
  };

  return (
    <div className="h-screen">
      <div
        className="hero-image w-full h-full bg-local relative bg-gray-400"
        style={{ backgroundImage: `url(${backgroundImg})` }}
      >
        <div className={`h-full`}>
          <div
            className={`flex flex-wrap w-full
              h-full 2xl:mb-6  ${
                type === "TALL" ? "items-end" : "2xl:place-content-center items-center"
              }`}
          >
            <div
              className={`${
                type === "WIDE"
                  ? "flex 2xl:h-5/6 2xl:w-full 2xl:justify-center place-content-center items-center 2xl:items-start h-full w-9/12"
                  : "h-full flex flex-col place-content-end justify-items-end justify-end w-full items-end"
              }`}
            >
              <div className={`${type === "WIDE" && "2xl:w-3/4"}`}>
                <EMLogo
                  className={`w-40 mb-8 2xl:mb-0 ml-10 2xl:w-60 2xl:h-60 ${
                    type === "WIDE"
                      ? "2xl:ml-20 2xl:block absolute px-10 bottom-5 right-5 2xl:static"
                      : "pl-1"
                  }`}
                  color={dashboard?.EMLogoColor}
                />
                {currentPage && dashboard && (
                  <SiteInformation currentPage={currentPage} dashboard={dashboard} />
                )}
              </div>

              <div
                className={`text-4xl w-full ${
                  type === "TALL" ? "h-1/2 px-7 pb-2" : "h-5/6 2xl:h-full 2xl:px-7 2xl:pb-2"
                }`}
              >
                <div className="2xl:hidden"></div>
                <AnimatePresence exitBeforeEnter>
                  {dashboard?.pages.map((page) => {
                    return currentPage && currentPage === page ? (
                      <DashboardPage
                        page={page}
                        animationDirection={animationDirection}
                        key={page.id}
                      />
                    ) : null;
                  })}
                </AnimatePresence>
              </div>

              {dashboard && currentPage && (
                <Menu
                  dashboard={dashboard}
                  currentPage={currentPage}
                  handleAnimationDirection={setAnimationDirection}
                  handlePageChange={handlePageChange}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TouchLayout;

const useDashboards = () => {
  const [dashboards, setDashboards] = useState<null | Dashboard[]>(null);
  const fb = useFirebase();

  useEffect(() => {
    fb.firestore()
      .collection("dashboardConfigurations")
      .get()
      .then((querySnap) => {
        const dashboards: Dashboard[] = [];
        querySnap.forEach((doc) => {
          dashboards.push(doc.data() as Dashboard);
        });
        setDashboards(dashboards);
      })
      .catch((err) => {
        console.log(err);
      });
  }, [fb]);

  return { dashboards };
};

type DashProps = {
  page: Page;
  animationDirection: "left" | "right";
};

export const DashboardPage = ({ page, animationDirection }: DashProps) => {
  const { type } = useGlobalState();
  const TallVariants = useMemo(() => {
    return {
      initial: {
        x: animationDirection === "left" ? "-100vw" : "100vw",
      },
      animate: {
        x: 0,
        y: 0,
        transition: {
          duration: 1,
          type: "spring",
        },
      },
      exit: {
        x: animationDirection === "left" ? "100vw" : "-100vw",
        transition: {
          duration: 0.2,
          type: "easeIn",
        },
      },
    };
  }, [animationDirection]);

  const WideVariants = useMemo(() => {
    return {
      initial: {
        y: animationDirection === "left" ? "-100vh" : "100vh",
      },
      animate: {
        x: 0,
        y: 0,
        transition: {
          duration: 1,
          type: "spring",
        },
      },
      exit: {
        y: animationDirection === "left" ? "100vh" : "-100vh",
        transition: {
          duration: 0.2,
          type: "easeIn",
        },
      },
    };
  }, [animationDirection]);

  if (page.type === "xlarge") return <XLargeCard />;
  if (page.type === "large")
    return (
      <motion.div
        variants={type === "TALL" ? TallVariants : WideVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        className="flex flex-wrap h-full"
      >
        <LargeCard component={page.component} />
      </motion.div>
    );
  if (page.type === "medium")
    return (
      <motion.div
        variants={type === "TALL" ? TallVariants : WideVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        className="flex flex-wrap h-full w-full"
      >
        <MediumCard component={page.componentTop} />
        <MediumCard component={page.componentBottom} />
      </motion.div>
    );
  if (page.type === "mediumSmall")
    return (
      <motion.div
        variants={type === "TALL" ? TallVariants : WideVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        className="flex flex-wrap h-full "
      >
        <SmallCard component={page.componentTopLeft} />
        <SmallCard component={page.componentTopRight} />
        <MediumCard component={page.componentBottom} />
      </motion.div>
    );
  if (page.type === "small")
    return (
      <motion.div
        variants={type === "TALL" ? TallVariants : WideVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        className="flex flex-wrap h-full"
      >
        <SmallCard component={page.componentTopLeft} />
        <SmallCard component={page.componentTopRight} />
        <SmallCard component={page.componentBottomLeft} />
        <SmallCard component={page.componentBottomRight} />
      </motion.div>
    );
  return <div>Could not find dashboard</div>;
};
